/**
 * Protected Layout - Modern Route Protection
 * 
 * This is the BEST PRACTICE approach for protecting routes in React Router v6.
 * Instead of wrapping each route with ProtectedRoute, we create a layout that
 * handles authentication at a higher level.
 * 
 * Benefits:
 * - Cleaner route configuration
 * - Single point of authentication check
 * - No conditional hook calls
 * - Better performance (check once, not per route)
 * - Prevents white screen crashes during auth checks
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import AuthenticatedLayout from './AuthenticatedLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import { selectIsAuthReady, selectIsAuthenticated } from '../store/slices/authSlice';

/**
 * ProtectedLayout Component
 * 
 * Acts as a "gatekeeper" - checks if user is authenticated before
 * allowing access to the AuthenticatedLayout and its children routes.
 * 
 * Flow:
 * 1. Check if auth system is ready (prevents race conditions)
 * 2. Check if user is authenticated
 * 3. If not authenticated → redirect to login
 * 4. If authenticated → render AuthenticatedLayout with protected routes
 */
const ProtectedLayout = () => {
  const location = useLocation();
  const isAuthReady = useSelector(selectIsAuthReady);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Step 1: Wait for auth system to initialize
  // This prevents checking authentication state before Redux is ready
  if (!isAuthReady) {
    return <LoadingSpinner fullPage size="lg" text="Oturum kontrol ediliyor..." />;
  }

  // Step 2: Check authentication
  // If not authenticated, redirect to login with return path
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Step 3: User is authenticated and auth is ready
  // Render the authenticated layout with all protected routes
  return <AuthenticatedLayout />;
};

export default ProtectedLayout;

